import { Component, computed, effect, forwardRef, inject, input, output, signal, untracked } from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { FaIconLibrary, FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import {
  NgbDate,
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerModule,
  NgbDateStruct,
} from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper, InputDateAdapter, InputDateFormatterAdapter } from "../../../../core/utils";
import { ParameterCatalog } from "../../../models";

@Component({
  selector: "adaa-form-input-date-range",
  imports: [TranslatePipe, ReactiveFormsModule, NgbDatepickerModule, FontAwesomeModule],
  templateUrl: "./form-input-date-range.html",
  styleUrl: "./form-input-date-range.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormInputDateRangeComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => FormInputDateRangeComponent),
    },
    {
      provide: NgbDateAdapter,
      useClass: InputDateAdapter,
    },
    {
      provide: NgbDateParserFormatter,
      useClass: InputDateFormatterAdapter,
    },
  ],
})
export class FormInputDateRangeComponent implements ControlValueAccessor {
  readonly AdaaHelper = AdaaHelper;

  private readonly _iconLibrary = inject(FaIconLibrary);
  private readonly _dateAdapter: NgbDateAdapter<number> = inject(NgbDateAdapter);
  private readonly _dateFormatAdapter = inject(NgbDateParserFormatter);
  private readonly _requiredFromValidator = signal<boolean>(false);
  private _onValidatorChange: () => void;
  private _onTouched: () => void;
  private _onChange: (value: string | null) => void;

  readonly required = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly label = input<string>("");
  readonly placeholder = input<string>("");
  readonly isDisabled = input<boolean>(false);
  readonly controlName = input<string | null>(null);
  readonly controlId = input<string | null>(null);
  readonly maxDate = input<NgbDateStruct | null>();
  readonly minDate = input<NgbDateStruct | null>();
  readonly setDefaultValue = input<string>();
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] }>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly inputChanges = output<any>();

  readonly effectiveControlId = computed(() => this.controlId() ?? this.controlName());
  readonly isRequired = computed(() => this.required() || this._requiredFromValidator());

  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate | null = null;
  toDate: NgbDate | null = null;
  formatedDate: string = "";

  constructor() {
    this._iconLibrary.addIcons(faCalendar);

    effect(() => {
      const value = this.setDefaultValue();
      if (value === undefined) return;
      untracked(() => {
        if (value) this.writeValue(value);
      });
    });

    effect(() => {
      const validatorList = this.setValidator();
      const name = this.controlName();
      if (validatorList?.parameterCatalogs) {
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === name);
        if (field) {
          untracked(() => {
            this._requiredFromValidator.set(field.mandatory === "Y");
            if (this._onValidatorChange) this._onValidatorChange();
          });
        }
      }
    });
  }

  public writeValue(obj: string): void {
    if (obj === null || obj === undefined) {
      this.fromDate = null;
      this.toDate = null;
      this.formatedDate = "";
      return;
    }

    const datesArr = obj.split(" - ");
    if (!AdaaHelper.isDefined(datesArr) || datesArr.length !== 2) return;

    if (AdaaHelper.isDefined(datesArr[0])) {
      const date = this._dateAdapter.fromModel(parseInt(datesArr[0]));
      if (date) {
        this.fromDate = new NgbDate(date.year, date.month, date.day);
      }
    }

    if (AdaaHelper.isDefined(datesArr[1])) {
      const date = this._dateAdapter.fromModel(parseInt(datesArr[1]));
      if (date) {
        this.toDate = new NgbDate(date.year, date.month, date.day);
      }
    }

    //This Time out to fix the date in the textbox
    setTimeout(() => {
      this.formatedDate = `${this._dateFormatAdapter.format(this.fromDate)} - ${this._dateFormatAdapter.format(this.toDate)}`;
    }, 500);
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  public setDisabledState?(_isDisabled: boolean): void {
    // This will be handled by the parent form control
  }

  public validate(control: FormControl): false | { required: boolean } {
    if (!this.isRequired()) return false;

    const value = control.value;
    const isRequired = value === null || value === "" || !this._isValidDateRange(value);

    return (
      isRequired && {
        required: true,
      }
    );
  }

  private _isValidDateRange(value: string): boolean {
    if (!value || typeof value !== "string") return false;

    const dates = value.split(" - ");
    if (dates.length !== 2) return false;

    const [startDate, endDate] = dates;
    return !!(startDate && endDate && startDate.trim() && endDate.trim());
  }

  public valueChanged(event: NgbDate): void {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = event;
    } else if (this.fromDate && !this.toDate && event?.after(this.fromDate)) {
      this.toDate = event;
    } else {
      this.toDate = null;
      this.fromDate = event;
    }

    this.formatedDate = `${this._dateFormatAdapter.format(this.fromDate)} - ${this._dateFormatAdapter.format(this.toDate)}`;

    const valueDate = `${this._dateAdapter.toModel(this.fromDate)} - ${this._dateAdapter.toModel(this.toDate)}`;

    // Notify parent form control
    if (this._onChange) this._onChange(valueDate);
    if (this._onTouched) this._onTouched();
    this.inputChanges.emit(valueDate);

    // Trigger validation
    if (this._onValidatorChange) this._onValidatorChange();
  }

  public isHovered(date: NgbDate) {
    return (
      this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate)
    );
  }

  public isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  public isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  public resetInput(): void {
    this.fromDate = null;
    this.toDate = null;
    this.formatedDate = "";

    if (this._onChange) this._onChange(null);
    if (this._onTouched) this._onTouched();
    this.inputChanges.emit(null);

    // Trigger validation
    if (this._onValidatorChange) this._onValidatorChange();
  }
}
